import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  ArrowLeft,
  Paperclip,
  Mic,
  SendHorizontal,
  Phone,
  Video,
  MoreVertical,
  CheckCheck,
  Users,
  X,
  FileText,
  Sparkles,
} from "lucide-react";
import Avatar from "./Avatar";
import { useChat } from "./chatStore";
import styles from "./chat.module.scss";

const dayLabel = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return "Bugün";
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Dün";
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const clock = (ts) => {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EmptyState = () => (
  <div className={styles.viewEmpty}>
    <div className={styles.viewEmptyIcon}>
      <Users size={30} />
    </div>
    <h3>Sohbetlerin</h3>
    <p>Soldan bir sohbet seç ya da yeni bir sohbet başlat.</p>
  </div>
);

const ConversationView = ({ conversation, onBack, mobile }) => {
  const { send, authorMeta } = useChat();
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  // Sohbet değişince taslağı sıfırla + en alta kaydır.
  useEffect(() => {
    setDraft("");
    setFiles([]);
  }, [conversation?.id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversation?.id, conversation?.messages?.length]);

  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    (conversation?.messages || []).forEach((msg) => {
      const d = new Date(msg.createdAt).toDateString();
      if (d !== lastDay) {
        out.push({ kind: "sep", id: `sep-${msg.id}`, label: dayLabel(msg.createdAt) });
        lastDay = d;
      }
      out.push({ kind: "msg", ...msg });
    });
    return out;
  }, [conversation]);

  if (!conversation) return <EmptyState />;

  const isGroup = conversation.type === "group";
  const isAI = conversation.type === "ai";
  const subtitle = isAI
    ? "her zaman çevrimiçi"
    : isGroup
    ? `${(conversation.participantIds?.length || 0) + 1} üye`
    : conversation.contact?.title || "çevrimiçi";

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []).map((f) => ({
      id: `f${Date.now()}-${f.name}`,
      name: f.name,
      type: f.type,
      size: f.size,
    }));
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const doSend = () => {
    if (!draft.trim() && files.length === 0) return;
    send(conversation.id, { text: draft, attachments: files });
    setDraft("");
    setFiles([]);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <div className={styles.viewPane}>
      <header className={styles.viewHeader}>
        {mobile && (
          <button type="button" className={styles.iconBtn} onClick={onBack} aria-label="Geri">
            <ArrowLeft size={20} />
          </button>
        )}
        <Avatar
          src={conversation.avatar}
          name={conversation.displayName}
          initials={conversation.initials}
          color={conversation.color}
          size={40}
          online={!isGroup && !isAI}
          square={isGroup}
          icon={isGroup ? <Users size={18} /> : isAI ? <Sparkles size={18} /> : null}
        />
        <div className={styles.viewHeaderMeta}>
          <span className={styles.viewHeaderName}>{conversation.displayName}</span>
          <span className={styles.viewHeaderStatus}>{subtitle}</span>
        </div>
        <div className={styles.viewHeaderActions}>
          <button type="button" className={styles.iconBtn} title="Sesli ara" aria-label="Sesli ara">
            <Phone size={18} />
          </button>
          <button type="button" className={styles.iconBtn} title="Görüntülü ara" aria-label="Görüntülü ara">
            <Video size={18} />
          </button>
          <button type="button" className={styles.iconBtn} title="Daha fazla" aria-label="Daha fazla">
            <MoreVertical size={18} />
          </button>
        </div>
      </header>

      <div className={styles.messages} ref={scrollRef}>
        {grouped.length === 0 ? (
          <div className={styles.firstMsgHint}>
            <p>
              <strong>{conversation.displayName}</strong> ile sohbete başla 👋
            </p>
          </div>
        ) : (
          grouped.map((item) =>
            item.kind === "sep" ? (
              <div key={item.id} className={styles.daySep}>
                <span>{item.label}</span>
              </div>
            ) : (
              <Bubble key={item.id} msg={item} isGroup={isGroup} authorMeta={authorMeta} />
            )
          )
        )}
      </div>

      {files.length > 0 && (
        <div className={styles.attachTray}>
          {files.map((f) => (
            <span key={f.id} className={styles.attachChip}>
              <FileText size={14} />
              <span className={styles.attachName}>{f.name}</span>
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                aria-label="Kaldır"
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <footer className={styles.composer}>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={onPickFiles}
        />
        <button
          type="button"
          className={styles.composerIcon}
          onClick={() => fileRef.current?.click()}
          title="Dosya ekle"
          aria-label="Dosya ekle"
        >
          <Paperclip size={20} />
        </button>
        <textarea
          ref={inputRef}
          className={styles.composerInput}
          value={draft}
          rows={1}
          placeholder="Bir mesaj yaz…"
          onChange={(e) => {
            setDraft(e.target.value);
            autoGrow(e.target);
          }}
          onKeyDown={onKeyDown}
        />
        {draft.trim() || files.length > 0 ? (
          <button
            type="button"
            className={`${styles.composerIcon} ${styles.composerSend}`}
            onClick={doSend}
            title="Gönder"
            aria-label="Gönder"
          >
            <SendHorizontal size={20} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.composerIcon}
            title="Sesli mesaj"
            aria-label="Sesli mesaj"
            onClick={() =>
              setFiles((prev) => [
                ...prev,
                { id: `f${Date.now()}`, name: `Sesli mesaj.aac`, type: "audio/aac", size: 0 },
              ])
            }
          >
            <Mic size={20} />
          </button>
        )}
      </footer>
    </div>
  );
};

const Bubble = ({ msg, isGroup, authorMeta }) => {
  const mine = msg.authorId === "me";
  const author = authorMeta(msg.authorId);
  return (
    <div className={`${styles.bubbleRow} ${mine ? styles.bubbleRowMine : ""}`}>
      <div className={`${styles.bubble} ${mine ? styles.bubbleMine : styles.bubbleTheirs}`}>
        {isGroup && !mine && (
          <span className={styles.bubbleAuthor}>
            {author.name}
          </span>
        )}
        {msg.attachments?.map((a) => (
          <span key={a.id} className={styles.bubbleAttach}>
            <FileText size={15} />
            {a.name}
          </span>
        ))}
        {msg.text && <span className={styles.bubbleText}>{msg.text}</span>}
        <span className={styles.bubbleMeta}>
          {clock(msg.createdAt)}
          {mine && <CheckCheck size={14} className={styles.bubbleTick} />}
        </span>
      </div>
    </div>
  );
};

export default ConversationView;
